import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Chip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useNavigate } from 'react-router';

import { getStudentScheduleOptions } from '@drivingdoc/api-client/generated/react-query';
import { StudentPeriodEnrollment } from 'types/students';

import TwoLineCell from 'components/DataGrid/CellRenderers/TwoLineCell';

import './styles.css';

dayjs.extend(localizedFormat);
dayjs.extend(calendar);

const columns: GridColDef<StudentPeriodEnrollment>[] = [
  {
    field: 'period_number',
    headerName: 'Period #',
    type: 'number',
    sortable: false,
    valueGetter: (value) => Number(value),
  },
  {
    field: 'date',
    headerName: 'Date',
    type: 'date',
    sortable: true,
    width: 150,
    valueGetter: (_value, row) => {
      if (row) {
        return row.course_period.start;
      }
      return null;
    },
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format('ll');
      }
      return null;
    },
  },
  {
    field: 'start',
    headerName: 'Start',
    type: 'string',
    sortable: false,
    width: 200,
    valueGetter: (_value, row) => {
      if (row.course_period.start) {
        return `${dayjs(row.course_period.start).format('LT')} - ${dayjs(row.course_period.end).format('LT')}`;
      }
      return null;
    },
  },
  {
    field: 'attendance',
    headerName: 'Attendance',
    type: 'singleSelect',
    sortable: false,
    width: 130,
    renderCell: (params) => {
      switch (params.value) {
        case 'present':
          return (
            <Chip
              sx={{ width: '100%!important', fontWeight: 'bold' }}
              icon={<CheckIcon fontSize="medium" />}
              size="small"
              label="Present"
              color="success"
              variant="outlined"
            />
          );
        case 'absent':
          return (
            <Chip
              sx={{ width: '100%!important', fontWeight: 'bold' }}
              icon={<CloseIcon fontSize="medium" />}
              size="small"
              label="No-Show"
              color="error"
              variant="outlined"
            />
          );
        case 'no_permit':
          return (
            <Chip
              sx={{ width: '100%!important', fontWeight: 'bold' }}
              icon={<CloseIcon fontSize="medium" />}
              size="small"
              label="No Permit"
              color="warning"
              variant="outlined"
            />
          );
      }
    },
  },
  {
    field: 'attendance_taken_at',
    headerName: 'Attendance Taken At',
    type: 'dateTime',
    sortable: false,
    width: 200,
    valueGetter: (value) => (!value ? null : new Date(value)),
    renderCell: (params) => {
      const enrollment = params.row;
      if (!enrollment.attendance_taken_at) {
        return;
      }
      return (
        <TwoLineCell
          textAlign="center"
          line1={`${enrollment.attendance_taken_by_user?.nickname ?? enrollment.attendance_taken_by_user?.first_name ?? ''} ${enrollment.attendance_taken_by_user?.last_name ?? ''}`}
          line2={`${dayjs(enrollment.attendance_taken_at).calendar(null, { sameElse: 'MMM D, YYYY [at] h:mm a' })}`}
        />
      );
    },
  },
  {
    field: 'created_at',
    headerName: 'Enrolled',
    type: 'dateTime',
    sortable: false,
    width: 225,
    valueGetter: (value) => (!value ? null : new Date(value)),
    renderCell: (params) => {
      const enrollment = params.row;
      if (!enrollment.created_at) {
        return;
      }
      return (
        <TwoLineCell
          textAlign="center"
          line1={`${enrollment.created_by_user?.nickname ?? enrollment.created_by_user?.first_name ?? ''} ${enrollment.created_by_user?.last_name ?? ''}`}
          line2={`${dayjs(enrollment.created_at).calendar(null, { sameElse: 'MMM D, YYYY [at] h:mm a' })}`}
        />
      );
    },
  },
  {
    field: 'canceled_at',
    headerName: 'Canceled',
    type: 'dateTime',
    sortable: false,
    width: 225,
    valueGetter: (value) => (!value ? null : new Date(value)),
    renderCell: (params) => {
      const enrollment = params.row;
      if (!enrollment.canceled_at) {
        return;
      }
      return (
        <TwoLineCell
          textAlign="center"
          line1={`${enrollment.canceled_by_user?.nickname ?? enrollment.canceled_by_user?.first_name ?? ''} ${enrollment.canceled_by_user?.last_name ?? ''}`}
          line2={`${dayjs(enrollment.canceled_at).calendar(null, { sameElse: 'MMM D, YYYY [at] h:mm a' })}`}
        />
      );
    },
  },
  {
    field: 'section',
    headerName: 'Section',
    type: 'string',
    sortable: true,
    width: 300,
    valueGetter: (_value, row) => {
      return row.course_period.course_section.title ?? '';
    },
  },
];

interface ScheduleProps {
  studentId: string;
}

export default function Schedule({ studentId }: ScheduleProps) {
  const navigate = useNavigate();
  const { data: studentPeriodEnrollmentsData, isLoading } = useQuery({
    ...getStudentScheduleOptions({
      path: {
        id: studentId,
      },
    }),
  });
  const studentPeriodEnrollments = studentPeriodEnrollmentsData as StudentPeriodEnrollment[] | undefined;

  return (
    <DataGrid<StudentPeriodEnrollment>
      getRowId={(row) => row.course_period_id}
      onRowClick={(params) => {
        if (params.row.course_period.course_section.course.type === 'behind_the_wheel') {
          //   openDriveOverviewDialog(params.row.course_period_id, params.row.student_user_id);
        } else {
          navigate(`/admin/periods/${params.row.course_period_id}`);
        }
      }}
      loading={isLoading}
      columns={columns}
      rows={studentPeriodEnrollments ?? []}
      getRowClassName={(params) => {
        return params.row.canceled_at ? 'datagrid__row--strikethrough' : '';
      }}
    />
  );
}
